import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Tooltip,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import { 
  Search, 
  FilterList,
  Refresh,
  Send,
  CheckCircle,
  Cancel,
  Schedule,
  Assessment,
  Email,
  MoreVert,
  Menu as MenuIcon,
  CalendarToday
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { getAuthHeader } from '../services/authService';
import { toast } from 'react-toastify';

interface AssignedSurvey {
  assignmentId: number;
  surveyId: number;
  surveyTitle: string;
  surveyCategory: string;
  studentId: number;
  studentName: string;
  studentEmail: string;
  assignedDate: string;
  dueDate?: string;
  completedDate?: string;
  status: string;
  score?: number;
  riskLevel?: string;
  totalQuestions: number;
  answeredQuestions: number;
  completionPercentage: number;
  notes?: string;
}

interface Statistics {
  totalAssignments: number;
  completedAssignments: number;
  pendingAssignments: number;
  inProgressAssignments: number;
  completionRate: number;
  uniqueSurveys: number;
  uniqueStudents: number;
}

const ViewAssignedSurveysPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // State
  const [assignedSurveys, setAssignedSurveys] = useState<AssignedSurvey[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [availableSurveys, setAvailableSurveys] = useState<any[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    page: 0,
    size: 10,
    search: '',
    status: '',
    surveyId: undefined as number | undefined,
    startDate: null as Date | null,
    endDate: null as Date | null
  });
  
  const [pagination, setPagination] = useState({
    totalElements: 0,
    totalPages: 0,
    currentPage: 0
  });

  // Dialog states
  const [selectedAssignment, setSelectedAssignment] = useState<AssignedSurvey | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [reassignNotes, setReassignNotes] = useState('');

  // Load data
  const loadAssignedSurveys = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: filters.page.toString(),
        size: filters.size.toString()
      });

      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.surveyId) params.append('surveyId', filters.surveyId.toString());
      if (filters.startDate) params.append('startDate', filters.startDate.toISOString());
      if (filters.endDate) params.append('endDate', filters.endDate.toISOString());

      const response = await axios.get(`/api/provider/assigned-surveys?${params}`, {
        headers: getAuthHeader()
      });

      setAssignedSurveys(response.data.content || []);
      setPagination({
        totalElements: response.data.totalElements || 0,
        totalPages: response.data.totalPages || 0,
        currentPage: response.data.page || 0
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load assigned surveys');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await axios.get('/api/provider/assigned-surveys/statistics', {
        headers: getAuthHeader()
      });
      setStatistics(response.data);
    } catch (err) {
      console.error('Failed to load statistics:', err);
    }
  };

  const loadAvailableSurveys = async () => {
    try {
      const response = await axios.get('/api/provider/assigned-surveys/surveys', {
        headers: getAuthHeader()
      });
      setAvailableSurveys(response.data);
    } catch (err) {
      console.error('Failed to load available surveys:', err);
    }
  };

  useEffect(() => {
    loadAssignedSurveys();
    loadStatistics();
    loadAvailableSurveys();
  }, [filters]);

  // Event handlers
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    let status = '';
    switch (newValue) {
      case 1: status = 'PENDING'; break;
      case 2: status = 'IN_PROGRESS'; break;
      case 3: status = 'COMPLETED'; break;
    }
    setFilters(prev => ({ ...prev, status, page: 0 }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, search: event.target.value, page: 0 }));
  };

  const handleSurveyChange = (event: any) => {
    setFilters(prev => ({ 
      ...prev, 
      surveyId: event.target.value === '' ? undefined : event.target.value,
      page: 0 
    }));
  };

  const handleStartDateChange = (date: Date | null) => {
    setFilters(prev => ({ ...prev, startDate: date, page: 0 }));
  };

  const handleEndDateChange = (date: Date | null) => {
    setFilters(prev => ({ ...prev, endDate: date, page: 0 }));
  };

  const handlePageChange = (event: any, newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage - 1 }));
  };

  const handleViewDetails = (assignment: AssignedSurvey) => {
    setSelectedAssignment(assignment);
    setShowDetailsDialog(true);
  };

  const handleSendReminder = async (assignment: AssignedSurvey) => {
    try {
      setLoading(true);
      await axios.post(`/api/provider/assigned-surveys/${assignment.assignmentId}/remind`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Reminder sent successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to send reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleReassign = (assignment: AssignedSurvey) => {
    setSelectedAssignment(assignment);
    setReassignNotes('');
    setShowReassignDialog(true);
  };

  const confirmReassign = async () => {
    if (!selectedAssignment) return;

    try {
      setLoading(true);
      await axios.post(`/api/provider/assigned-surveys/${selectedAssignment.assignmentId}/reassign`, {
        notes: reassignNotes
      }, {
        headers: getAuthHeader()
      });
      setShowReassignDialog(false);
      setSelectedAssignment(null);
      toast.success('Survey reassigned successfully');
      loadAssignedSurveys();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to reassign survey');
    } finally {
      setLoading(false);
    }
  };

  const handleViewStudentProfile = (studentId: number) => {
    navigate(`/students/${studentId}`);
  };

  const handleViewSurveyResults = (assignment: AssignedSurvey) => {
    if (assignment.status === 'COMPLETED') {
      navigate(`/assessments/${assignment.surveyId}/student/${assignment.studentId}/results`);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED': return <CheckCircle color="success" />;
      case 'IN_PROGRESS': return <Schedule color="warning" />;
      case 'PENDING': return <Cancel color="error" />;
      default: return null;
    }
  };

  const getRiskLevelColor = (riskLevel?: string) => {
    switch (riskLevel) {
      case 'RED': return 'error';
      case 'YELLOW': return 'warning';
      case 'GREEN': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            View Assigned Surveys
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Button color="inherit" onClick={logout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Assigned Surveys Overview
            </Typography>
            <Button onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
              ← Back to Dashboard
            </Button>
          </Box>
          <IconButton onClick={loadAssignedSurveys} disabled={loading}>
            <Refresh />
          </IconButton>
        </Box>

        {/* Statistics Cards */}
        {statistics && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Assignments
                  </Typography>
                  <Typography variant="h4">
                    {statistics.totalAssignments}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Completed
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {statistics.completedAssignments}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={statistics.completionRate}
                    sx={{ mt: 1 }}
                  />
                  <Typography variant="caption">
                    {statistics.completionRate.toFixed(1)}% completion rate
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    In Progress
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {statistics.inProgressAssignments}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Pending
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {statistics.pendingAssignments}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  placeholder="Search by student or survey..."
                  value={filters.search}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Survey</InputLabel>
                  <Select
                    value={filters.surveyId || ''}
                    label="Survey"
                    onChange={handleSurveyChange}
                  >
                    <MenuItem value="">All Surveys</MenuItem>
                    {availableSurveys.map(survey => (
                      <MenuItem key={survey.id} value={survey.id}>
                        {survey.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={handleStartDateChange}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  onChange={handleEndDateChange}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {/* Tabs */}
        <Paper sx={{ mb: 2 }}>
          <Tabs value={selectedTab} onChange={handleTabChange}>
            <Tab label={`All (${pagination.totalElements})`} />
            <Tab label="Pending" icon={<Cancel />} />
            <Tab label="In Progress" icon={<Schedule />} />
            <Tab label="Completed" icon={<CheckCircle />} />
          </Tabs>
        </Paper>

        {/* Assignments Table */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Survey</TableCell>
                  <TableCell>Assigned Date</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Progress</TableCell>
                  <TableCell align="center">Score</TableCell>
                  <TableCell align="center">Risk Level</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignedSurveys.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body1" color="textSecondary">
                        No assigned surveys found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  assignedSurveys.map((assignment) => (
                    <TableRow key={assignment.assignmentId} hover>
                      <TableCell>
                        <Box
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleViewStudentProfile(assignment.studentId)}
                        >
                          <Typography variant="body1">{assignment.studentName}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {assignment.studentEmail}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Assessment color="primary" />
                          <Box>
                            <Typography variant="body2">{assignment.surveyTitle}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {assignment.surveyCategory}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(assignment.assignedDate)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          {getStatusIcon(assignment.status)}
                          <Typography variant="body2">
                            {assignment.status}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box>
                          <Typography variant="body2">
                            {assignment.answeredQuestions}/{assignment.totalQuestions}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={assignment.completionPercentage}
                            sx={{ mt: 0.5, height: 4, width: 60, mx: 'auto' }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {assignment.score !== undefined ? assignment.score : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {assignment.riskLevel ? (
                          <Chip
                            label={assignment.riskLevel}
                            color={getRiskLevelColor(assignment.riskLevel)}
                            size="small"
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(assignment)}
                            >
                              <MoreVert />
                            </IconButton>
                          </Tooltip>
                          {assignment.status === 'COMPLETED' && (
                            <Tooltip title="View Results">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewSurveyResults(assignment)}
                              >
                                <Assessment />
                              </IconButton>
                            </Tooltip>
                          )}
                          {assignment.status !== 'COMPLETED' && (
                            <>
                              <Tooltip title="Send Reminder">
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => handleSendReminder(assignment)}
                                >
                                  <Email />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reassign">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => handleReassign(assignment)}
                                >
                                  <Send />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage + 1}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </Card>
      </Container>

      {/* Assignment Details Dialog */}
      <Dialog
        open={showDetailsDialog}
        onClose={() => setShowDetailsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assignment Details</DialogTitle>
        <DialogContent>
          {selectedAssignment && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Student</Typography>
                  <Typography variant="body1">{selectedAssignment.studentName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Survey</Typography>
                  <Typography variant="body1">{selectedAssignment.surveyTitle}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Assigned Date</Typography>
                  <Typography variant="body1">{formatDate(selectedAssignment.assignedDate)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Typography variant="body1">{selectedAssignment.status}</Typography>
                </Grid>
                {selectedAssignment.completedDate && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Completed Date</Typography>
                    <Typography variant="body1">{formatDate(selectedAssignment.completedDate)}</Typography>
                  </Grid>
                )}
                {selectedAssignment.score !== undefined && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Score</Typography>
                    <Typography variant="body1">{selectedAssignment.score}</Typography>
                  </Grid>
                )}
                {selectedAssignment.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Notes</Typography>
                    <Typography variant="body1">{selectedAssignment.notes}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reassign Dialog */}
      <Dialog
        open={showReassignDialog}
        onClose={() => setShowReassignDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reassign Survey</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to reassign the survey "{selectedAssignment?.surveyTitle}" to {selectedAssignment?.studentName}?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (optional)"
            value={reassignNotes}
            onChange={(e) => setReassignNotes(e.target.value)}
            placeholder="Add any notes about why you're reassigning this survey..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReassignDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={confirmReassign}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Reassign'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ViewAssignedSurveysPage;